"use client";

import { useContext } from "react";
import { AuthContext } from "./provider";
import type { AuthLeague } from "./types";

/**
 * Access the full auth context (user, leagues, loading state, and actions).
 *
 * Must be used inside an `<AuthProvider>`.
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, login, logout } = useAuth();
 * ```
 */
export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

/**
 * Shortcut hook that returns just the active league (or `null`).
 *
 * Useful in components that only need the current league selection
 * without the rest of the auth state.
 *
 * @example
 * ```tsx
 * const activeLeague = useActiveLeague();
 * if (!activeLeague) return <p>No league selected</p>;
 * ```
 */
export function useActiveLeague(): AuthLeague | null {
  const { activeLeague } = useAuth();
  return activeLeague;
}
